import React, { useMemo } from 'react';

import Image from 'components/uiLibrary/Image';
import { TextButton } from 'components/uiLibrary/LinkButton';
import SpriteIcon from 'components/uiLibrary/SpriteIcon';
import Typography from 'components/uiLibrary/Typography';

import CommonTrans from 'components/Globals/CommonTrans';

import { TP } from 'constants/index';
import { SUBSCRIPTION_PRODUCT_IDS, SUBSCRIPTIONS_STATUSES } from 'constants/subscriptions';

import useAppContext from 'utils/hooks/useAppContext';
import { useProfileAccesses, useUserSubscriptions } from 'utils/hooks/useAuthenticatedUser';
import useDeviceTypeLayouts from 'utils/hooks/useDeviceTypeLayouts';
import useNavigate from 'utils/hooks/useNavigate';

import { useTranslation } from 'src/i18n';

import { ROUTE_RESERVED_KEYWORDS } from 'operabase-router/constants';

import LogoIcon from 'public/images/ob-icon.svg';

import classes from './StickyPaywallFooter.module.scss';

function StickyPaywallFooter({ onClick }) {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const { isLoggedIn, paywall } = useAppContext();
  const navigate = useNavigate();
  const { isMobile, isDesktop } = useDeviceTypeLayouts();
  const { data: userSubscriptions } = useUserSubscriptions();
  const { ownerProfile } = useProfileAccesses();

  const ownerProfileSubscription = userSubscriptions?.find(
    sub =>
      sub.status === SUBSCRIPTIONS_STATUSES.active &&
      sub.subscription?.product?.id &&
      [SUBSCRIPTION_PRODUCT_IDS.PROFILE_MONTHLY, SUBSCRIPTION_PRODUCT_IDS.PROFILE_ANNUAL].includes(
        sub.subscription.product.id,
      ) &&
      sub.profile?.id === ownerProfile?.profile?.id,
  );

  const hasAnnualSubscription =
    ownerProfileSubscription?.subscription?.product?.id === SUBSCRIPTION_PRODUCT_IDS.PROFILE_ANNUAL;

  const hasActiveSubscription = useMemo(
    () => userSubscriptions?.some(subscription => subscription?.status === SUBSCRIPTIONS_STATUSES.active),
    [userSubscriptions],
  );

  const loginLinkProps = useMemo(
    () => navigate.getLinkProps({ baseRoute: ROUTE_RESERVED_KEYWORDS.login, onlyLinkProps: true }),
    [navigate],
  );

  return (
    <div
      className={classes.root}
      onClick={e => {
        e.stopPropagation();
        onClick();
      }}
    >
      <div className={classes.container}>
        <div className={classes.container__left}>
          <Image
            src={LogoIcon}
            alt="Operabase Home"
            lazy={false}
            className={classes.logoIcon}
            disableCloudinary
            disableNextImage
          />
          {isLoggedIn && hasActiveSubscription && (
            <Typography size={isMobile ? 12 : 16}>
              {hasAnnualSubscription
                ? t(`${TP}.LIMITED_ACCESS_TITLE_HAS_SUB`)
                : t(`${TP}.LIMITED_ACCESS_TITLE_NO_ACCESS`)}
            </Typography>
          )}
          {isLoggedIn && !hasActiveSubscription && (
            <Typography size={isMobile ? 12 : 16}>
              {t(`${TP}.FREE_PLAN_TITLE_CURRENTLY`)}{' '}
              <Typography size={isMobile ? 12 : 16} color="orange">
                ({t(`${TP}.FREE_PLAN_OPTIONS`)})
              </Typography>
            </Typography>
          )}
          {!isLoggedIn && (
            <>
              <Typography size={isMobile ? 12 : 16} weight="bold">
                {t(`${TP}.OPERABASE_CONTINUE_LOGIN`)} {t(`${TP}.FREE_TO_USE`)}
              </Typography>
              <TextButton
                rightIcon={<SpriteIcon icon="chevron_right" size={16} />}
                {...loginLinkProps}
                preventDefault
                isLink
              >
                {t(`${TP}.LOGIN_REGISTER`)}
              </TextButton>
            </>
          )}
        </div>
        <div className={classes.container__right}>
          {paywall?.paywallType && isLoggedIn && (
            <Typography variant="p" size={14} weight="medium">
              {isDesktop && <>{t(`${TP}.PAYWALL_COLLAPSED_TITLE`)} | </>}
              <Typography size={isMobile ? 12 : 14} weight="medium" color="link">
                {t(`${TP}.SEE_PLAN`)} <SpriteIcon icon="chevron_up" size={isMobile ? 12 : 14} />
              </Typography>
            </Typography>
          )}
          {(!paywall?.paywallType || !isLoggedIn) && isDesktop && (
            <CommonTrans
              i18nKey={`${TP}.FN_CONVERSION_CASTING_BANNER_ITEMS`}
              ns="NS_APP_GLOBALS"
              components={{
                ul: <ul className={classes.genreList} />,
                li: <li />,
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default StickyPaywallFooter;
